/**
 * DashboardCompactChart Component
 *
 * Compact chart card for Recursos Dashboard sidebar with dual-segment bar chart or line chart.
 * Displays label, value, percentage change, and mini chart (bar or line).
 *
 * Design specs from Figma:
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 30px
 * - Height: 227px (bar chart) or 209px (line chart)
 * - Bar chart: Dual-segment bar chart (5 groups, 2 bars each: 23px width)
 * - Bar colors: Purple (#7846FF) and Light Purple (#BFB1FF)
 * - Line chart: 2 lines (Purple #7846FF and Blue #4285F4)
 * - Percentage badge with trend color
 * - Legend at bottom with 2 items
 *
 * Used for 3 sidebar metrics:
 * - Saldo (Entradas/Saídas) - Bar
 * - Recursos Captados (Completos/Repelidos) - Bar
 * - Balanço (Completos/Repelidos) - Line
 */

import { CompactChartData } from "@/types/recursos-dashboard.interface";

interface DashboardCompactChartProps {
  data: CompactChartData;
}

/**
 * Formats number as Brazilian Real currency
 * Example: 250000 → "R$ 250.000,00"
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Simple SVG Line Chart Component
 * Renders 2 lines (purple and blue) with smooth curve
 */
function LineChart() {
  return (
    <svg width="100%" height="47" viewBox="0 0 345 47" preserveAspectRatio="xMidYMid meet" className="mt-[5px]">
      {/* Purple line - downward trend */}
      <polyline
        points="0,8 43,18 86,22 129,30 172,35 215,38 258,40 301,42 345,45"
        fill="none"
        stroke="#7846FF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Blue line - upward trend */}
      <polyline
        points="0,35 43,28 86,20 129,15 172,12 215,10 258,8 301,6 345,4"
        fill="none"
        stroke="#4285F4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Baseline */}
      <line x1="0" y1="46" x2="345" y2="46" stroke="#3D4C6B" strokeWidth="1" />
    </svg>
  );
}

export function DashboardCompactChart({ data }: DashboardCompactChartProps) {
  const isPositive = data.trend === 'up';
  const chartType = data.chartType || 'bar';

  // Generate 5 bar heights (simplified visualization - first bar full, second bar partial)
  const bars = data.data.slice(0, 5).map(() => ({
    bar1: 40, // Full height bar (first segment)
    bar2: 25, // Partial height bar (second segment)
  }));

  return (
    <div className="bg-[#15203C] rounded-lg p-4 flex flex-col">
      {/* Header with Badge */}
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-[#F6F6F6]">
            {data.label}
          </h3>
          <div className="bg-[#0C1427] rounded-full px-2 py-0.5 flex items-center justify-center">
            <p
              className={`text-[10px] ${
                isPositive ? 'text-[#13D480]' : 'text-[#DC4426]'
              }`}
            >
              {isPositive ? '+' : ''}{data.percentage}%
            </p>
          </div>
        </div>
      </div>

      {/* Value */}
      <p className="text-base font-bold text-white mb-3">
        {formatCurrency(data.value)}
      </p>

      {/* Chart Container */}
      {chartType === 'line' ? (
        // Line Chart
        <div className="flex-1 min-h-0 mb-3">
          <LineChart />
        </div>
      ) : (
        // Dual-Segment Bar Chart
        <div className="flex items-end gap-4 h-10 mb-3">
          {bars.map((bar, idx) => (
            <div key={idx} className="flex gap-1 items-end">
              {/* First segment - Purple */}
              <div
                className="bg-[#7846FF] w-4 rounded-t"
                style={{ height: `${bar.bar1}px` }}
              />
              {/* Second segment - Light Purple */}
              <div
                className="bg-[#BFB1FF] w-4 rounded-t"
                style={{ height: `${bar.bar2}px` }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="size-1.5 rounded-full" style={{ backgroundColor: data.legend.color1 }} />
            <p className="text-[10px] text-white">{data.legend.label1}</p>
          </div>
          <p className="text-[10px] text-white">{data.legend.value1}%</p>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="size-1.5 rounded-full" style={{ backgroundColor: data.legend.color2 }} />
            <p className="text-[10px] text-white">{data.legend.label2}</p>
          </div>
          <p className="text-[10px] text-white">{data.legend.value2}%</p>
        </div>
      </div>
    </div>
  );
}
